import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ContactTable = ({ contacts, onContactSelect, onBulkAction, selectedContacts, onContactToggle }) => {
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [expandedRows, setExpandedRows] = useState(new Set());

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const toggleRowExpansion = (contactId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded?.has(contactId)) {
      newExpanded?.delete(contactId);
    } else {
      newExpanded?.add(contactId);
    }
    setExpandedRows(newExpanded);
  };

  const sortedContacts = [...contacts]?.sort((a, b) => {
    const aValue = a?.[sortField];
    const bValue = b?.[sortField];
    const direction = sortDirection === 'asc' ? 1 : -1;
    
    if (typeof aValue === 'string') {
      return aValue?.localeCompare(bValue) * direction;
    }
    return (aValue - bValue) * direction;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return 'text-success bg-green-50';
      case 'Inactive': return 'text-muted-foreground bg-gray-50';
      case 'Pending': return 'text-warning bg-amber-50';
      default: return 'text-muted-foreground bg-gray-50';
    }
  };

  const getEngagementColor = (level) => {
    switch (level) {
      case 'High': return 'text-success';
      case 'Medium': return 'text-warning';
      case 'Low': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Table Header */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted border-b border-border">
            <tr>
              <th className="w-12 px-4 py-3">
                <input
                  type="checkbox"
                  className="rounded border-border"
                  onChange={(e) => onBulkAction(e?.target?.checked ? 'selectAll' : 'deselectAll')}
                  checked={selectedContacts?.length === contacts?.length && contacts?.length > 0}
                />
              </th>
              <th className="text-left px-4 py-3">
                <button
                  onClick={() => handleSort('name')}
                  className="flex items-center space-x-1 text-sm font-medium text-foreground hover:text-primary"
                >
                  <span>Contact</span>
                  <Icon 
                    name={sortField === 'name' ? (sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} 
                    size={14} 
                  />
                </button>
              </th>
              <th className="text-left px-4 py-3">
                <button
                  onClick={() => handleSort('outlet')}
                  className="flex items-center space-x-1 text-sm font-medium text-foreground hover:text-primary"
                >
                  <span>Outlet</span>
                  <Icon 
                    name={sortField === 'outlet' ? (sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} 
                    size={14} 
                  />
                </button>
              </th>
              <th className="text-left px-4 py-3">
                <span className="text-sm font-medium text-foreground">Beat</span>
              </th>
              <th className="text-left px-4 py-3">
                <span className="text-sm font-medium text-foreground">Location</span>
              </th>
              <th className="text-left px-4 py-3">
                <button
                  onClick={() => handleSort('engagement')}
                  className="flex items-center space-x-1 text-sm font-medium text-foreground hover:text-primary"
                >
                  <span>Engagement</span>
                  <Icon 
                    name={sortField === 'engagement' ? (sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} 
                    size={14} 
                  />
                </button>
              </th>
              <th className="text-left px-4 py-3">
                <span className="text-sm font-medium text-foreground">Status</span>
              </th>
              <th className="w-24 px-4 py-3">
                <span className="text-sm font-medium text-foreground">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sortedContacts?.map((contact) => (
              <React.Fragment key={contact?.id}>
                <tr className="hover:bg-muted/50 transition-colors duration-200">
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      className="rounded border-border"
                      checked={selectedContacts?.includes(contact?.id)}
                      onChange={() => onContactToggle(contact?.id)}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center space-x-3">
                      <Image
                        src={contact?.avatar}
                        alt={contact?.avatarAlt}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <div>
                        <div className="font-medium text-foreground">{contact?.name}</div>
                        <div className="text-sm text-muted-foreground">{contact?.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-foreground">{contact?.outlet}</div>
                    <div className="text-sm text-muted-foreground">{contact?.outletType}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {contact?.beats?.slice(0, 2)?.map((beat, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-secondary/10 text-secondary"
                        >
                          {beat}
                        </span>
                      ))}
                      {contact?.beats?.length > 2 && (
                        <span className="text-xs text-muted-foreground">
                          +{contact?.beats?.length - 2} more
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm text-foreground">{contact?.location}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className={`text-sm font-medium ${getEngagementColor(contact?.engagement)}`}>
                      {contact?.engagement}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {contact?.successRate}% success rate
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(contact?.status)}`}>
                      {contact?.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center space-x-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => toggleRowExpansion(contact?.id)}
                        className="h-8 w-8"
                      >
                        <Icon 
                          name={expandedRows?.has(contact?.id) ? "ChevronUp" : "ChevronDown"} 
                          size={16} 
                        />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onContactSelect(contact)}
                        className="h-8 w-8"
                      >
                        <Icon name="Mail" size={16} />
                      </Button>
                    </div>
                  </td>
                </tr>
                
                {/* Expanded Row Details */}
                {expandedRows?.has(contact?.id) && (
                  <tr>
                    <td colSpan="8" className="px-4 py-4 bg-muted/30">
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Contact Details */}
                        <div>
                          <h4 className="font-medium text-foreground mb-3">Contact Information</h4>
                          <div className="space-y-2 text-sm">
                            <div className="flex items-center space-x-2">
                              <Icon name="Phone" size={14} className="text-muted-foreground" />
                              <span className="text-foreground">{contact?.phone}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Icon name="Twitter" size={14} className="text-muted-foreground" />
                              <span className="text-foreground">{contact?.twitter}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Icon name="Linkedin" size={14} className="text-muted-foreground" />
                              <span className="text-foreground">{contact?.linkedin}</span>
                            </div>
                          </div>
                          
                          <div className="mt-4">
                            <h5 className="font-medium text-foreground mb-2">All Beats</h5>
                            <div className="flex flex-wrap gap-1">
                              {contact?.beats?.map((beat, index) => (
                                <span
                                  key={index}
                                  className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-secondary/10 text-secondary"
                                >
                                  {beat}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                        
                        {/* Interaction History */}
                        <div>
                          <h4 className="font-medium text-foreground mb-3">Recent Interactions</h4>
                          <div className="space-y-3">
                            {contact?.recentInteractions?.map((interaction, index) => (
                              <div key={index} className="flex items-start space-x-3 p-3 bg-card rounded-md border border-border">
                                <div className={`w-2 h-2 rounded-full mt-2 ${
                                  interaction?.type === 'email' ? 'bg-primary' :
                                  interaction?.type === 'call'? 'bg-success' : 'bg-warning'
                                }`} />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between">
                                    <span className="text-sm font-medium text-foreground capitalize">
                                      {interaction?.type}
                                    </span>
                                    <span className="text-xs text-muted-foreground">
                                      {interaction?.date}
                                    </span>
                                  </div>
                                  <p className="text-sm text-muted-foreground mt-1">
                                    {interaction?.subject}
                                  </p>
                                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium mt-2 ${
                                    interaction?.status === 'Responded' ? 'bg-green-50 text-success' :
                                    interaction?.status === 'Opened'? 'bg-blue-50 text-primary' : 'bg-gray-50 text-muted-foreground'
                                  }`}>
                                    {interaction?.status}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                      
                      {/* Notes Section */}
                      <div className="mt-6">
                        <h4 className="font-medium text-foreground mb-3">Notes & Preferences</h4>
                        <div className="bg-card p-4 rounded-md border border-border">
                          <p className="text-sm text-muted-foreground">
                            {contact?.notes}
                          </p>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ContactTable;